import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Edit, Info, Search, ArrowUpDown, ArrowUp, ArrowDown, AlertCircle, X, CheckCircle } from 'lucide-react';
import UserDetailsModal from './UserDetailsModal';

const INPUT_CLS = 'w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-600 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/40 focus:border-cyan-500/40 transition-all';
const LABEL_CLS = 'block text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1.5';

const roleBadge = (role = '') => {
  if (role === 'superadmin') return 'bg-purple-500/10 border-purple-500/20 text-purple-400';
  if (role === 'admin')      return 'bg-blue-500/10 border-blue-500/20 text-blue-400';
  return 'bg-white/5 border-white/10 text-gray-400';
};

const SortIcon = ({ col, sortConfig }) => {
  if (sortConfig.key !== col) return <ArrowUpDown size={11} className="text-gray-600" />;
  return sortConfig.direction === 'ascending'
    ? <ArrowUp size={11} className="text-cyan-400" />
    : <ArrowDown size={11} className="text-cyan-400" />;
};

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'descending' });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', username: '', email: '', role: 'user', totalBalance: '' });
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editMsg, setEditMsg] = useState(null);

  const { token } = useAuth();

  useEffect(() => {
    if (!token) return;
    axios.get('/api/users', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { setUsers(r.data); setLoading(false); })
      .catch(e => { setError(e); setLoading(false); });
  }, [token]);

  const handleUserClick = async (user) => {
    setIsModalOpen(true);
    setLoadingDetails(true);
    try {
      const res = await fetch(`/api/users/${user._id}/details`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setUserDetails(await res.json());
    } catch {
      setUserDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  const closeModal = () => { setIsModalOpen(false); setUserDetails(null); };

  const openEdit = (user) => {
    setEditUser(user);
    setEditForm({
      name: user.name || '',
      username: user.username || '',
      email: user.email || '',
      role: user.role || 'user',
      totalBalance: user.totalBalance != null ? user.totalBalance.toString() : '',
    });
    setEditMsg(null);
  };

  const closeEdit = () => { setEditUser(null); setEditMsg(null); };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editUser || !token) return;
    setEditSubmitting(true);
    setEditMsg(null);
    try {
      const res = await axios.put(`/api/users/${editUser._id}`, {
        name: editForm.name,
        username: editForm.username,
        email: editForm.email,
        role: editForm.role,
        totalBalance: parseFloat(editForm.totalBalance) || 0,
      }, { headers: { Authorization: `Bearer ${token}` } });
      const updated = res.data.user || res.data;
      setUsers(prev => prev.map(u => u._id === editUser._id ? { ...u, ...updated } : u));
      setEditMsg({ type: 'success', text: 'User updated.' });
      setTimeout(closeEdit, 1400);
    } catch (err) {
      setEditMsg({ type: 'error', text: err.response?.data?.msg || err.message });
    } finally {
      setEditSubmitting(false);
    }
  };

  const requestSort = (key) => {
    setSortConfig(prev =>
      prev.key === key && prev.direction === 'ascending'
        ? { key, direction: 'descending' }
        : { key, direction: 'ascending' }
    );
  };

  const filtered = React.useMemo(() => {
    const term = searchTerm.toLowerCase();
    return [...users]
      .filter(u =>
        (u.username?.toLowerCase().includes(term)) ||
        (u.email?.toLowerCase().includes(term)) ||
        (u.mobile?.toLowerCase().includes(term)) ||
        (u.name?.toLowerCase().includes(term)) ||
        (u.country?.toLowerCase().includes(term))
      )
      .sort((a, b) => {
        const dir = sortConfig.direction === 'ascending' ? 1 : -1;
        if (a[sortConfig.key] < b[sortConfig.key]) return -dir;
        if (a[sortConfig.key] > b[sortConfig.key]) return dir;
        return 0;
      });
  }, [users, searchTerm, sortConfig]);

  if (loading) return (
    <div className="space-y-3">
      <div className="h-10 w-64 rounded-xl bg-white/4 animate-pulse mb-6" />
      {[...Array(8)].map((_, i) => <div key={i} className="h-12 rounded-xl bg-white/4 animate-pulse" />)}
    </div>
  );

  if (error) return (
    <div className="flex items-center gap-2 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
      <AlertCircle size={15} /> Error loading users: {error.message}
    </div>
  );

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Users</h1>
          <p className="text-sm text-gray-500 mt-1">{filtered.length} of {users.length} user{users.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="relative w-full sm:w-72">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="Search by name, username, email, mobile, country…"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-600 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-500/30 transition-all"
          />
        </div>
      </div>

      <div className="rounded-2xl bg-white/3 border border-white/8 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-white/6">
                <th className="px-5 py-3.5 text-left">
                  <button onClick={() => requestSort('username')} className="flex items-center gap-1.5 text-[11px] font-bold text-gray-500 uppercase tracking-wider hover:text-gray-300 transition-colors">
                    User <SortIcon col="username" sortConfig={sortConfig} />
                  </button>
                </th>
                <th className="px-4 py-3.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider hidden md:table-cell">Mobile</th>
                <th className="px-4 py-3.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Email</th>
                <th className="px-4 py-3.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider hidden xl:table-cell">Country</th>
                <th className="px-4 py-3.5 text-left">
                  <button onClick={() => requestSort('totalBalance')} className="flex items-center gap-1.5 text-[11px] font-bold text-gray-500 uppercase tracking-wider hover:text-gray-300 transition-colors">
                    Balance <SortIcon col="totalBalance" sortConfig={sortConfig} />
                  </button>
                </th>
                <th className="px-4 py-3.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider hidden md:table-cell">Today</th>
                <th className="px-4 py-3.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Role</th>
                <th className="px-4 py-3.5 text-right text-[11px] font-bold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/4">
              {filtered.length === 0 ? (
                <tr><td colSpan={8} className="py-16 text-center text-gray-600 text-sm">No users found.</td></tr>
              ) : filtered.map(user => (
                <tr key={user._id} className="hover:bg-white/[0.02] transition-colors group">
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500/40 to-purple-500/40 border border-white/10 flex items-center justify-center text-xs font-bold text-white shrink-0">
                        {(user.username || 'U').charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-white truncate max-w-[120px]">{user.username}</div>
                        <div className="text-[10px] text-gray-600 hidden sm:block">
                          {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3.5 text-sm text-gray-400 hidden md:table-cell">{user.mobile || '—'}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-400 hidden lg:table-cell truncate max-w-[180px]">{user.email}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-400 hidden xl:table-cell">{user.country || '—'}</td>
                  <td className="px-4 py-3.5">
                    <span className="text-sm font-semibold text-green-400">
                      ${user.totalBalance !== undefined ? user.totalBalance.toFixed(2) : '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-sm text-blue-400 hidden md:table-cell">
                    ${user.todayProfit !== undefined ? user.todayProfit.toFixed(2) : '—'}
                  </td>
                  <td className="px-4 py-3.5 hidden lg:table-cell">
                    <span className={`inline-flex px-2 py-0.5 rounded-md text-[10px] font-bold border uppercase tracking-wider ${roleBadge(user.role)}`}>
                      {user.role || 'user'}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        onClick={() => handleUserClick(user)}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 text-gray-400 text-xs hover:text-white hover:bg-white/8 transition-all"
                        title="Details"
                      >
                        <Info size={12} />
                        <span className="hidden sm:inline">View</span>
                      </button>
                      <button
                        onClick={() => openEdit(user)}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 text-gray-400 text-xs hover:text-cyan-400 hover:bg-cyan-500/8 hover:border-cyan-500/20 transition-all"
                        title="Edit"
                      >
                        <Edit size={12} />
                        <span className="hidden sm:inline">Edit</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {isModalOpen && (
        <UserDetailsModal onClose={closeModal} loading={loadingDetails} userDetails={userDetails} />
      )}

      {/* Edit User Modal */}
      {editUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-[#0d1526] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-white/8">
              <h2 className="text-base font-bold text-white">Edit User</h2>
              <button onClick={closeEdit} className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-white/8 transition-all">
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="p-5 space-y-4">
              {editMsg && (
                <div className={`flex items-center gap-2 p-3 rounded-xl text-sm border ${
                  editMsg.type === 'success'
                    ? 'bg-green-500/10 border-green-500/20 text-green-400'
                    : 'bg-red-500/10 border-red-500/20 text-red-400'
                }`}>
                  {editMsg.type === 'success' ? <CheckCircle size={13} /> : <AlertCircle size={13} />}
                  {editMsg.text}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={LABEL_CLS}>Full Name</label>
                  <input type="text" value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} className={INPUT_CLS} />
                </div>
                <div>
                  <label className={LABEL_CLS}>Username</label>
                  <input type="text" value={editForm.username} onChange={e => setEditForm(p => ({ ...p, username: e.target.value }))} className={INPUT_CLS} required />
                </div>
                <div className="col-span-2">
                  <label className={LABEL_CLS}>Email</label>
                  <input type="email" value={editForm.email} onChange={e => setEditForm(p => ({ ...p, email: e.target.value }))} className={INPUT_CLS} required />
                </div>
                <div>
                  <label className={LABEL_CLS}>Role</label>
                  <select
                    value={editForm.role}
                    onChange={e => setEditForm(p => ({ ...p, role: e.target.value }))}
                    className={`${INPUT_CLS} appearance-none`}
                  >
                    <option value="user" className="bg-[#0d1526]">User</option>
                    <option value="admin" className="bg-[#0d1526]">Admin</option>
                    <option value="superadmin" className="bg-[#0d1526]">Superadmin</option>
                  </select>
                </div>
                <div>
                  <label className={LABEL_CLS}>Balance ($)</label>
                  <input type="number" step="0.01" min="0" value={editForm.totalBalance} onChange={e => setEditForm(p => ({ ...p, totalBalance: e.target.value }))} className={INPUT_CLS} />
                </div>
              </div>

              <div className="flex gap-3 pt-1">
                <button
                  type="submit"
                  disabled={editSubmitting}
                  className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-sm font-semibold disabled:opacity-50 hover:opacity-90 transition-all shadow-lg shadow-cyan-500/15"
                >
                  {editSubmitting ? 'Saving…' : 'Save Changes'}
                </button>
                <button type="button" onClick={closeEdit} className="flex-1 py-2.5 rounded-xl bg-white/5 border border-white/10 text-gray-300 text-sm font-semibold hover:bg-white/8 transition-all">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
